import { Link } from "react-router-dom";

/**
 * Matrimonial Success Reward Program terms. Built on the app's RewardTermsConditions screen
 * (Mjmarry/src/components/rewards/RewardTermsConditions.js), with website additions: the
 * 20-couple limit (section 2) and extra eligibility, anti-fraud and liability safeguards.
 * Keep the app's screen in step with any change made here.
 */
const TERMS = [
  {
    title: "1. Program Overview",
    content:
      'The Matrimonial Success Reward Program ("Program") offers a one-time reward of $100 USD per couple to eligible users who successfully meet and marry through our platform. The Program is limited to the first twenty (20) approved couples (see Section 2).',
  },
  {
    title: "2. Limited Availability (Maximum 20 Couples)",
    content: "The Program is limited to a maximum of twenty (20) rewarded couples in total.",
    bullets: [
      "Claims are reviewed in the order in which complete claims are received",
      "Once twenty (20) claims have been approved, the Program closes automatically, even if it is still shown on our website, in the app or in other promotional material",
      "Submitting a claim does not reserve a place or guarantee a reward",
      "Claims received after the Program closes will not be considered",
    ],
  },
  {
    title: "3. Eligibility Criteria",
    content: "To qualify for the reward, both individuals must meet ALL of the following conditions:",
    bullets: [
      "Must have initially connected through our platform",
      "Must provide valid usernames/profile IDs used on the platform",
      "Must submit clear screenshots of conversations demonstrating that the connection originated on the platform",
      "Both individuals must be verified members at the time of connection",
      "Names on profiles must match government-issued identification, with at least the last name matching",
      "First names may remain confidential if requested, subject to verification",
      "Both individuals must be at least 18 years old",
      "The marriage must be lawful where it took place and must have taken place after the couple first connected on our platform",
      "Both individuals must consent to the claim, and both accounts must be in good standing, with no violations of our Terms & Conditions or Community Guidelines",
      "Employees, contractors and representatives of MJMarry, and their immediate family members, are not eligible",
      "Each individual may be part of only one rewarded claim",
    ],
  },
  {
    title: "4. Proof of Marriage (Verification Only)",
    content: "To confirm eligibility, couples may be required to provide:",
    bullets: ["Proof of marriage (e.g., marriage certificate or equivalent documentation)"],
    important: [
      "This documentation is used strictly for verification purposes only",
      "It will NOT be used for testimonials, marketing, or promotional content",
      "It will be handled confidentially and not retained longer than necessary",
    ],
  },
  {
    title: "5. Mandatory Testimonial Requirement",
    content: "To receive the reward, participation in a testimonial is mandatory. Couples must provide:",
    bullets: [
      "Two (2) short videos from their marriage (e.g., ceremony, celebration, or related events)",
      "At least four (4) photographs from their marriage",
    ],
    subContent:
      "These testimonials help build trust and encourage other users by showcasing real success stories from the platform.",
    agreement: [
      "Your submitted videos, photographs, and story may be used worldwide, irrevocably, royalty-free, and without additional compensation for marketing, promotional, and advertising purposes",
      "Content may be published on our platform, social media, and related channels",
      "You confirm that both parties consent to sharing the content",
      "You confirm that you own or have permission to share the submitted content, including permission from anyone else who appears in it",
    ],
  },
  {
    title: "6. Privacy & Data Handling",
    content:
      "Sensitive documents (including marriage proof and IDs) are collected only for verification purposes. Such documents will:",
    bullets: [
      "Never be used in testimonials or marketing",
      "Be securely handled and not stored longer than necessary",
    ],
    note: "We take reasonable steps to protect your data, though absolute security cannot be guaranteed.",
  },
  {
    title: "7. Reward Payment Terms",
    content: "The $100 reward is issued per couple, not per individual. Payment will be made via:",
    bullets: ["Bank transfer", "PayPal", "Cash pickup (where applicable)"],
    subContent:
      "For cash pickup, a valid government-issued ID is required. The name must match the platform profile to prevent fraud. The reward is paid in US dollars, and any fees charged by the bank or payment provider, including currency conversion fees, are the recipient's responsibility.",
    warning: "Failure to provide valid payment details may result in delay or forfeiture of the reward.",
    deadline:
      "Rewards must be claimed within 6 months of the verified marriage date. Claims submitted after this period may be denied.",
  },
  {
    title: "8. Tax Responsibility",
    content:
      "The reward may be considered taxable income under applicable law. Participants are solely responsible for reporting and paying any taxes arising from the reward.",
  },
  {
    title: "9. Fraud Prevention & Verification",
    content: "We reserve the right to:",
    bullets: [
      "Request additional documentation or verification",
      "Reject applications that are incomplete, inconsistent, or suspected of fraud",
      "Reject duplicate, automated, bulk or spam submissions without review",
      "Recover any reward paid on the basis of false, misleading or fraudulent information",
      "Make all decisions about eligibility and approval at our sole discretion; these decisions are final",
    ],
    denyReasons: [
      "The relationship did not originate through the platform",
      "Platform rules were violated",
      "Submitted information is false or misleading",
    ],
    warning: "Any misuse of the Program may result in disqualification and account suspension or termination.",
  },
  {
    title: "10. Program Limitations",
    bullets: [
      "Only one reward per couple is allowed",
      "The Program may be modified, paused, or terminated at any time without prior notice",
      "Rewards are non-transferable and non-exchangeable",
      "No purchase or payment is necessary to take part. The Program is not a lottery, contest or game of chance",
      "The Program is void where prohibited or restricted by law",
    ],
  },
  {
    title: "11. Liability Disclaimer",
    bullets: [
      "Participation is voluntary",
      "We are not responsible for disputes between participants",
      "We are not liable for any losses arising from participation in the Program",
      "We do not guarantee that any user will meet a spouse or marry through the platform",
      "To the fullest extent permitted by law, our total liability in connection with the Program will not exceed the amount of the reward",
    ],
  },
  {
    title: "12. Acceptance of Terms",
    content: "By applying for the reward, participants confirm that they:",
    bullets: [
      "Agree to these Terms & Conditions",
      "Provide accurate and truthful information",
      "Consent to mandatory testimonial participation (including marriage videos and photographs)",
      "Consent to the irrevocable use of submitted testimonial content as described",
      "Understand that they are responsible for any taxes arising from the reward",
      "Agree that rewards must be claimed within 6 months of marriage",
      "Understand that the Program is limited to the first twenty (20) approved couples",
    ],
  },
  {
    title: "13. Relationship to Our Terms & Conditions",
    content: (
      <>
        These Program terms are in addition to MJMarry's <Link to="/termsandconditions">Terms &amp; Conditions</Link>,
        which continue to govern your use of the platform. If the two conflict, these Program terms apply to the
        Program. We may update these terms at any time, and the version on this page applies to claims submitted
        after it is posted.
      </>
    ),
  },
];

function List({ items }) {
  return (
    <ul>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export default function MarriageRewardTerms() {
  return (
    <>
      <p>
        <strong>Matrimonial Success Reward Program</strong> — $100 USD Reward Program, limited to the first 20
        approved couples
      </p>
      {TERMS.map((section) => (
        <section key={section.title}>
          <h2>{section.title}</h2>
          {section.content && <p>{section.content}</p>}
          {section.bullets && <List items={section.bullets} />}
          {section.important && (
            <div className="legal-callout">
              <p>
                <strong>Important:</strong>
              </p>
              <List items={section.important} />
            </div>
          )}
          {section.subContent && (
            <p>
              <em>{section.subContent}</em>
            </p>
          )}
          {section.agreement && (
            <>
              <p>
                <strong>By participating, you agree that:</strong>
              </p>
              <List items={section.agreement} />
            </>
          )}
          {section.denyReasons && (
            <>
              <p>
                <strong>Rewards may be denied if:</strong>
              </p>
              <List items={section.denyReasons} />
            </>
          )}
          {section.warning && <p className="legal-callout legal-callout--warning">{section.warning}</p>}
          {section.deadline && <p className="legal-callout">{section.deadline}</p>}
          {section.note && (
            <p>
              <em>{section.note}</em>
            </p>
          )}
        </section>
      ))}
    </>
  );
}
